import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/utils/useAuth";
import { Upload, X, FileText, User, Users, Search, Loader2, Activity, AlertTriangle, FileCheck, ClipboardList, MapPin as RoomIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { HierarchicalTypeSelector } from "./HierarchicalTypeSelector";
import { SapDatePicker } from "@/components/ui/sap-date-picker";
import { SapTimePicker } from "@/components/ui/sap-time-picker";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";

interface FieldOption {
  value: string;
  label: string;
  labelAr?: string;
}

interface FieldValidation {
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  patternMessage?: string;
  accept?: string;
  maxSizeMB?: number;
  maxFiles?: number;
}

interface ShowCondition {
  field: string;
  operator?: "equals" | "not_equals" | "in" | "not_empty" | "empty";
  value?: string | string[] | boolean;
}

interface FormField {
  id: number;
  code: string;
  name: string;
  nameAr?: string;
  fieldType: string;
  isRequired: boolean;
  displayOrder: number;
  columnSpan: number;
  placeholder?: string;
  placeholderAr?: string;
  helpText?: string;
  helpTextAr?: string;
  defaultValue?: string;
  options?: FieldOption[];
  optionsSource?: "static" | "api" | "dependent" | 
    "countries" | "regions" | "cities" | 
    "sites" | "zones" | "areas" | 
    "departments" | "groups" | "users" | "contractors" | 
    "request_types" | "approval_roles" | 
    "user_sites" | "user_groups" | "user_departments" | "user_profile" | "material_types";
  optionsApi?: string;
  dependsOnField?: string;
  filterByField?: string;
  validation?: FieldValidation;
  showCondition?: ShowCondition;
}

interface FieldRendererProps {
  field: FormField;
  value: any;
  onChange: (value: any, meta?: { autoFill?: Record<string, any> }) => void;
  formValues: Record<string, any>;
  disabled?: boolean;
  error?: string;
  maxDurationDays?: number;
}

/** Maps legacy api/dependent location fields to master-data sources used by getDataSourceOptions */
function resolveLocationDynamicSource(field: FormField): { source: string; filterField?: string } | null {
  const os = field.optionsSource || "";
  const api = (field.optionsApi || "").toLowerCase();
  const code = field.code || "";

  if (code === "country" || os === "countries" || (os === "api" && api.includes("countries"))) {
    return { source: "countries" };
  }
  if (os === "regions" || (os === "dependent" && api.includes("regions"))) {
    return { source: "regions", filterField: field.filterByField || "country" };
  }
  if (os === "cities" || (os === "dependent" && api.includes("cities"))) {
    return { source: "cities", filterField: field.filterByField || "country" };
  }
  if (os === "sites" || (os === "dependent" && api.includes("sites"))) {
    // Sites filter by city (which cascades from country)
    return { source: "sites", filterField: field.filterByField || "city" };
  }
  if (os === "zones" || (os === "dependent" && api.includes("zones"))) {
    return { source: "zones", filterField: field.filterByField || "site" };
  }
  if (os === "areas" || (os === "dependent" && api.includes("areas"))) {
    return { source: "areas", filterField: field.filterByField || "zone" };
  }
  return null;
}

async function fetchDataSourceOptionsJson(
  source: string,
  filterValue?: string
): Promise<Array<{ value: string; label: string }> | undefined> {
  const input = {
    "0": {
      json: {
        source,
        ...(filterValue ? { filterValue: String(filterValue) } : {}),
      },
    },
  };
  const response = await fetch(
    `/api/trpc/requestConfig.fields.getDataSourceOptions?batch=1&input=${encodeURIComponent(JSON.stringify(input))}`,
    { credentials: "include" }
  );
  const result = await response.json();
  return result[0]?.result?.data?.json;
}

// Site Type Field Component
function SiteTypeField({
  field,
  value,
  onChange,
  disabled,
  error,
  isRTL,
  getLabel,
  getHelpText,
}: {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  disabled: boolean;
  error?: string;
  isRTL: boolean;
  getLabel: () => string;
  getHelpText: () => string | undefined;
}) {
  const { data: siteTypes, isLoading } = trpc.masterData.getSiteTypes.useQuery();

  if (isLoading) {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
          {getLabel()}
          {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
        </Label>
        <div className="flex items-center justify-center p-4 border rounded-md">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      </div>
    );
  }

  const types = (siteTypes || []).map((t: any) => ({
    id: t.id,
    parentId: t.parentId ?? null,
    code: t.code,
    name: t.name,
    nameAr: t.nameAr ?? null,
    level: t.level ?? 0,
    sortOrder: t.sortOrder ?? 0,
    isActive: t.isActive !== false,
  }));

  return (
    <div className="space-y-1.5">
      <HierarchicalTypeSelector
        label={getLabel()}
        types={types}
        value={value?.id ?? value ?? null}
        onChange={(id, path) => onChange({ id, path })}
        required={field.isRequired}
        isRTL={isRTL}
      />
      {getHelpText() && (
        <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
      )}
      {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
    </div>
  );
}

// Zone Type Field Component
function ZoneTypeField({
  field,
  value,
  onChange,
  disabled,
  error,
  isRTL,
  getLabel,
  getHelpText,
}: {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  disabled: boolean;
  error?: string;
  isRTL: boolean;
  getLabel: () => string;
  getHelpText: () => string | undefined;
}) {
  const { data: zoneTypes, isLoading } = trpc.masterData.getZoneTypes.useQuery();

  if (isLoading) {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
          {getLabel()}
          {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
        </Label>
        <div className="flex items-center justify-center p-4 border rounded-md">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      </div>
    );
  }

  const types = (zoneTypes || []).map((t: any) => ({
    id: t.id,
    parentId: t.parentId ?? null,
    code: t.code,
    name: t.name,
    nameAr: t.nameAr ?? null,
    level: t.level ?? 0,
    sortOrder: t.sortOrder ?? 0,
    isActive: t.isActive !== false,
  }));

  return (
    <div className="space-y-1.5">
      <HierarchicalTypeSelector
        label={getLabel()}
        types={types}
        value={value?.id ?? value ?? null}
        onChange={(id, path) => onChange({ id, path })}
        required={field.isRequired}
        isRTL={isRTL}
      />
      {getHelpText() && (
        <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
      )}
      {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
    </div>
  );
}

// Area Type Field Component
function AreaTypeField({
  field,
  value,
  onChange,
  disabled,
  error,
  isRTL,
  getLabel,
  getHelpText,
}: {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  disabled: boolean;
  error?: string;
  isRTL: boolean;
  getLabel: () => string;
  getHelpText: () => string | undefined;
}) {
  const { data: areaTypes, isLoading } = trpc.masterData.getAreaTypes.useQuery();

  if (isLoading) {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
          {getLabel()}
          {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
        </Label>
        <div className="flex items-center justify-center p-4 border rounded-md">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      </div>
    );
  }

  const types = (areaTypes || []).map((t: any) => ({
    id: t.id,
    parentId: t.parentId ?? null,
    code: t.code,
    name: t.name,
    nameAr: t.nameAr ?? null,
    level: t.level ?? 0,
    sortOrder: t.sortOrder ?? 0,
    isActive: t.isActive !== false,
  }));

  return (
    <div className="space-y-1.5">
      <HierarchicalTypeSelector
        label={getLabel()}
        types={types}
        value={value?.id ?? value ?? null}
        onChange={(id, path) => onChange({ id, path })}
        required={field.isRequired}
        isRTL={isRTL}
      />
      {getHelpText() && (
        <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
      )}
      {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
    </div>
  );
}

// Searchable Dropdown Field Component (for site/zone/area fields with many options)
function SearchableDropdownField({
  field,
  value,
  onChange,
  disabled,
  error,
  isWaitingForParent,
  parentFieldName,
  isLoadingOptions,
  options,
  getLabel,
  getPlaceholder,
  getHelpText,
  getOptionLabel,
  baseInputClass,
}: {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  disabled: boolean;
  error?: string;
  isWaitingForParent: boolean;
  parentFieldName: string;
  isLoadingOptions: boolean;
  options: FieldOption[];
  getLabel: () => string;
  getPlaceholder: () => string | undefined;
  getHelpText: () => string | undefined;
  getOptionLabel: (opt: FieldOption) => string;
  baseInputClass: string;
}) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [comboOpen, setComboOpen] = useState(false);
  const allOpts = options.filter((opt) => opt.value !== "" && opt.value != null);
  const selectedLabel = allOpts.find(o => String(o.value) === String(value || ''));

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
        {getLabel()}
        {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
      </Label>
      <Popover open={comboOpen} onOpenChange={setComboOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={comboOpen}
            className={cn(baseInputClass, "justify-between font-normal h-auto min-h-[2.5rem]", isWaitingForParent && "opacity-60")}
            disabled={disabled || isLoadingOptions || isWaitingForParent}
          >
            <span className="truncate">
              {isLoadingOptions ? (
                <span className="flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Loading...</span>
              ) : selectedLabel ? (
                getOptionLabel(selectedLabel)
              ) : (
                <span className="text-muted-foreground">{isWaitingForParent ? (isRTL ? `اختر ${parentFieldName} أولاً` : `Select ${parentFieldName} first`) : (getPlaceholder() || t("common.select", "Select..."))}</span>
              )}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder={`Search ${getLabel().toLowerCase()}...`} />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {allOpts.map((opt) => (
                  <CommandItem
                    key={opt.value}
                    value={getOptionLabel(opt)}
                    onSelect={() => {
                      onChange(String(opt.value));
                      setComboOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", String(value) === String(opt.value) ? "opacity-100" : "opacity-0")} />
                    {getOptionLabel(opt)}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {getHelpText() && (
        <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
      )}
      {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
    </div>
  );
}

// User Lookup Field Component with search functionality
function UserLookupField({
  field,
  value,
  onChange,
  disabled,
  error,
  isRTL,
  getLabel,
  getPlaceholder,
  getHelpText,
}: {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  disabled: boolean;
  error?: string;
  isRTL: boolean;
  getLabel: () => string;
  getPlaceholder: () => string | undefined;
  getHelpText: () => string | undefined;
}) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);

  // Debounced search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        // Use proper tRPC batch format with credentials
        const input = {
          "0": {
            json: {
              source: "users",
              search: searchQuery,
            }
          }
        };
        const response = await fetch(
          `/api/trpc/requestConfig.fields.getDataSourceOptions?batch=1&input=${encodeURIComponent(JSON.stringify(input))}`,
          { credentials: 'include' }
        );
        const result = await response.json();
        if (result[0]?.result?.data?.json) {
          setSearchResults(result[0].result.data.json);
          setShowResults(true);
        }
      } catch (error) {
        console.error("User search failed:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSelectUser = (user: any) => {
    onChange({
      id: user.value,
      name: user.label,
      email: user.email || "",
    });
    setSearchQuery("");
    setShowResults(false);
  };

  const handleClear = () => {
    onChange(null);
    setSearchQuery("");
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
        {getLabel()}
        {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
      </Label>
      
      {value?.id ? (
        // Selected user display
        <div className="flex items-center gap-2 p-2 bg-[#E8DCF5] border border-[#5B2C93] rounded-md">
          <User className="h-4 w-4 text-[#5B2C93]" />
          <div className="flex-1">
            <p className="text-sm font-medium text-[#5B2C93]">{value.name}</p>
            {value.email && (
              <p className="text-xs text-[#5B2C93]">{value.email}</p>
            )}
          </div>
          {!disabled && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-[#5B2C93] hover:text-[#FF6B6B] hover:bg-[#FFE5E5]"
              onClick={handleClear}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ) : (
        // Search input
        <div className="relative">
          <div className="relative">
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={getPlaceholder() || t("common.searchUser", "Search by name or email...")}
              disabled={disabled}
              className="pr-10"
              onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {isSearching ? (
                <Loader2 className="h-4 w-4 animate-spin text-[#9CA3AF]" />
              ) : (
                <Search className="h-4 w-4 text-[#9CA3AF]" />
              )}
            </div>
          </div>
          
          {/* Search results dropdown */}
          {showResults && searchResults.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
              {searchResults.map((user) => (
                <button
                  key={user.value}
                  type="button"
                  className="w-full px-3 py-2 text-left hover:bg-[#F5F5F5] flex items-center gap-2 border-b last:border-b-0"
                  onClick={() => handleSelectUser(user)}
                >
                  <User className="h-4 w-4 text-[#9CA3AF]" />
                  <div>
                    <p className="text-sm font-medium">{user.label}</p>
                    {user.email && (
                      <p className="text-xs text-[#6B6B6B]">{user.email}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          
          {showResults && searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
            <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg p-3 text-center text-sm text-[#6B6B6B]">
              {t("common.noUsersFound", "No users found")}
            </div>
          )}
        </div>
      )}
      
      {getHelpText() && (
        <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
      )}
      {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
    </div>
  );
}

// Multi-User Lookup Field Component - allows selecting multiple users
function MultiUserLookupField({
  field,
  value,
  onChange,
  disabled,
  error,
  isRTL,
  getLabel,
  getPlaceholder,
  getHelpText,
}: {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  disabled: boolean;
  error?: string;
  isRTL: boolean;
  getLabel: () => string;
  getPlaceholder: () => string | undefined;
  getHelpText: () => string | undefined;
}) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);

  // Ensure value is always an array
  const selectedUsers: Array<{ id: string; name: string; email?: string }> = Array.isArray(value) ? value : [];

  // Debounced search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const input = {
          "0": {
            json: {
              source: "users",
              search: searchQuery,
            }
          }
        };
        const response = await fetch(
          `/api/trpc/requestConfig.fields.getDataSourceOptions?batch=1&input=${encodeURIComponent(JSON.stringify(input))}`,
          { credentials: 'include' }
        );
        const result = await response.json();
        if (result[0]?.result?.data?.json) {
          // Filter out already selected users
          const selectedIds = selectedUsers.map(u => u.id);
          const filtered = result[0].result.data.json.filter(
            (u: any) => !selectedIds.includes(u.value)
          );
          setSearchResults(filtered);
          setShowResults(true);
        }
      } catch (error) {
        console.error("User search failed:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, selectedUsers.length]);

  const handleSelectUser = (user: any) => {
    const newUser = {
      id: user.value,
      name: user.label,
      email: user.email || "",
    };
    onChange([...selectedUsers, newUser]);
    setSearchQuery("");
    setShowResults(false);
  };

  const handleRemoveUser = (userId: string) => {
    onChange(selectedUsers.filter(u => u.id !== userId));
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
        {getLabel()}
        {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
      </Label>

      {/* Selected users as badges */}
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedUsers.map((u) => (
            <Badge
              key={u.id}
              variant="secondary"
              className="text-xs flex items-center gap-1 bg-[#E8DCF5] text-[#5B2C93] border border-[#5B2C93] px-2 py-1"
            >
              <User className="h-3 w-3" />
              {u.name}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveUser(u.id)}
                  className="ml-0.5 hover:text-[#FF6B6B]"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}

      {/* Search input */}
      {!disabled && (
        <div className="relative">
          <div className="relative">
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={getPlaceholder() || t("common.searchUsers", "Search users by name or email...")}
              className="pr-10"
              onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {isSearching ? (
                <Loader2 className="h-4 w-4 animate-spin text-[#9CA3AF]" />
              ) : (
                <Search className="h-4 w-4 text-[#9CA3AF]" />
              )}
            </div>
          </div>

          {/* Search results dropdown */}
          {showResults && searchResults.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
              {searchResults.map((user) => (
                <button
                  key={user.value}
                  type="button"
                  className="w-full px-3 py-2 text-left hover:bg-[#F5F5F5] flex items-center gap-2 border-b last:border-b-0"
                  onClick={() => handleSelectUser(user)}
                >
                  <User className="h-4 w-4 text-[#9CA3AF]" />
                  <div>
                    <p className="text-sm font-medium">{user.label}</p>
                    {user.email && (
                      <p className="text-xs text-[#6B6B6B]">{user.email}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {showResults && searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
            <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg p-3 text-center text-sm text-[#6B6B6B]">
              {t("common.noUsersFound", "No users found")}
            </div>
          )}
        </div>
      )}

      {getHelpText() && (
        <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
      )}
      {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
    </div>
  );
}

// Activity Selector Field Component - shows main activity and sub-activity with requirements
function ActivitySelectorField({
  field,
  value,
  onChange,
  onRequirementsChange,
  disabled,
  error,
  isRTL,
  getLabel,
  getHelpText,
}: {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  onRequirementsChange?: (requirements: { needsRFC: boolean; needsHRS: boolean; needsMOP: boolean; needsMHV: boolean; needsRoomSelection: boolean }) => void;
  disabled: boolean;
  error?: string;
  isRTL: boolean;
  getLabel: () => string;
  getHelpText: () => string | undefined;
}) {
  const { t } = useTranslation();
  const [selectedMainActivityId, setSelectedMainActivityId] = useState<number | null>(value?.mainActivityId || null);
  const [selectedSubActivityId, setSelectedSubActivityId] = useState<number | null>(value?.subActivityId || null);

  const { data: mainActivities, isLoading: loadingMain } = trpc.masterData.getAllMainActivities.useQuery();
  const { data: subActivities, isLoading: loadingSub } = trpc.masterData.getAllSubActivities.useQuery();

  // Filter sub-activities based on selected main activity
  const filteredSubActivities = useMemo(() => {
    if (!selectedMainActivityId || !subActivities) return [];
    return subActivities.filter((s: any) => s.mainActivityId === selectedMainActivityId && s.isActive);
  }, [selectedMainActivityId, subActivities]);

  // Get selected sub-activity details for requirements
  const selectedSubActivity = useMemo(() => {
    if (!selectedSubActivityId || !subActivities) return null;
    return subActivities.find((s: any) => s.id === selectedSubActivityId);
  }, [selectedSubActivityId, subActivities]);

  // Update parent when selection changes
  useEffect(() => {
    if (selectedMainActivityId && selectedSubActivityId) {
      const mainActivity = mainActivities?.find((m: any) => m.id === selectedMainActivityId);
      const subActivity = subActivities?.find((s: any) => s.id === selectedSubActivityId);
      onChange({
        mainActivityId: selectedMainActivityId,
        mainActivityName: mainActivity?.name,
        subActivityId: selectedSubActivityId,
        subActivityName: subActivity?.name,
        requirements: {
          needsRFC: subActivity?.needsRFC || false,
          needsHRS: subActivity?.needsHRS || false,
          needsMOP: subActivity?.needsMOP || false,
          needsMHV: subActivity?.needsMHV || false,
          needsRoomSelection: subActivity?.needsRoomSelection || false,
        }
      });
      // Notify parent about requirements
      if (onRequirementsChange && subActivity) {
        onRequirementsChange({
          needsRFC: subActivity.needsRFC || false,
          needsHRS: subActivity.needsHRS || false,
          needsMOP: subActivity.needsMOP || false,
          needsMHV: subActivity.needsMHV || false,
          needsRoomSelection: subActivity.needsRoomSelection || false,
        });
      }
    }
  }, [selectedMainActivityId, selectedSubActivityId, mainActivities, subActivities]);

  // Reset sub-activity when main activity changes
  useEffect(() => {
    if (selectedMainActivityId !== value?.mainActivityId) {
      setSelectedSubActivityId(null);
    }
  }, [selectedMainActivityId]);

  if (loadingMain) {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
          {getLabel()}
          {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
        </Label>
        <div className="flex items-center justify-center p-4 border rounded-md">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Main Activity Selector */}
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
          <Activity className="h-3.5 w-3.5" />
          {isRTL ? "النشاط الرئيسي" : "Main Activity"}
          {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
        </Label>
        <Select
          value={selectedMainActivityId?.toString() || ""}
          onValueChange={(v) => setSelectedMainActivityId(parseInt(v))}
          disabled={disabled}
        >
          <SelectTrigger className={error ? "border-[#FF6B6B]" : ""}>
            <SelectValue placeholder={isRTL ? "اختر النشاط الرئيسي" : "Select main activity"} />
          </SelectTrigger>
          <SelectContent>
            {mainActivities?.filter((a: any) => a.isActive).map((activity: any) => (
              <SelectItem key={activity.id} value={activity.id.toString()}>
                {isRTL && activity.nameAr ? activity.nameAr : activity.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Sub-Activity Selector */}
      {selectedMainActivityId && (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
            <ClipboardList className="h-3.5 w-3.5" />
            {isRTL ? "النشاط الفرعي" : "Sub-Activity"}
            {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
          </Label>
          {loadingSub ? (
            <div className="flex items-center justify-center p-4 border rounded-md">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : (
            <Select
              value={selectedSubActivityId?.toString() || ""}
              onValueChange={(v) => setSelectedSubActivityId(parseInt(v))}
              disabled={disabled || filteredSubActivities.length === 0}
            >
              <SelectTrigger className={error ? "border-[#FF6B6B]" : ""}>
                <SelectValue placeholder={isRTL ? "اختر النشاط الفرعي" : "Select sub-activity"} />
              </SelectTrigger>
              <SelectContent>
                {filteredSubActivities.map((sub: any) => (
                  <SelectItem key={sub.id} value={sub.id.toString()}>
                    {isRTL && sub.nameAr ? sub.nameAr : sub.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {filteredSubActivities.length === 0 && !loadingSub && (
            <p className="text-xs text-[#6B6B6B]">
              {isRTL ? "لا توجد أنشطة فرعية لهذا النشاط الرئيسي" : "No sub-activities available for this main activity"}
            </p>
          )}
        </div>
      )}

      {/* Requirements Display */}
      {selectedSubActivity && (
        <div className="p-3 bg-[#E8DCF5] border border-[#5B2C93] rounded-md space-y-2">
          <Label className="text-xs font-medium text-[#5B2C93] uppercase flex items-center gap-1">
            <FileCheck className="h-3.5 w-3.5" />
            {isRTL ? "المتطلبات" : "Requirements"}
          </Label>
          <div className="flex flex-wrap gap-2">
            {selectedSubActivity.needsRFC && (
              <Badge className="bg-[#E8DCF5] text-[#5B2C93] hover:bg-[#E8DCF5]">
                <AlertTriangle className="h-3 w-3 mr-1" />
                RFC
              </Badge>
            )}
            {selectedSubActivity.needsHRS && (
              <Badge className="bg-[#E8DCF5] text-[#5B2C93] hover:bg-[#E8DCF5]">
                <FileCheck className="h-3 w-3 mr-1" />
                HRS
              </Badge>
            )}
            {selectedSubActivity.needsMOP && (
              <Badge className="bg-[#FEF3C7] text-[#D97706] hover:bg-[#FEF3C7]">
                <ClipboardList className="h-3 w-3 mr-1" />
                MOP
              </Badge>
            )}
            {selectedSubActivity.needsMHV && (
              <Badge className="bg-[#D1FAE5] text-[#059669] hover:bg-[#D1FAE5]">
                <FileCheck className="h-3 w-3 mr-1" />
                MHV
              </Badge>
            )}
            {selectedSubActivity.needsRoomSelection && (
              <Badge className="bg-[#E8DCF5] text-[#5B2C93] hover:bg-[#E8DCF5]">
                <RoomIcon className="h-3 w-3 mr-1" />
                {isRTL ? "اختيار الغرفة" : "Room Selection"}
              </Badge>
            )}
            {!selectedSubActivity.needsRFC && !selectedSubActivity.needsHRS && !selectedSubActivity.needsMOP && !selectedSubActivity.needsMHV && !selectedSubActivity.needsRoomSelection && (
              <span className="text-xs text-[#5B2C93]">
                {isRTL ? "لا توجد متطلبات إضافية" : "No additional requirements"}
              </span>
            )}
          </div>
        </div>
      )}

      {getHelpText() && (
        <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
      )}
      {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
    </div>
  );
}

export function FieldRenderer({
  field,
  value,
  onChange,
  formValues,
  disabled = false,
  error,
  maxDurationDays,
}: FieldRendererProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { user } = useAuth();
  const [dynamicOptions, setDynamicOptions] = useState<FieldOption[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  // Keep a ref to the latest value so async callbacks always check the current state
  const valueRef = useRef(value);
  valueRef.current = value;
  // Track the last filter value used to load options — prevents re-fetching when filter hasn't changed
  const lastLoadedFilterRef = useRef<string | undefined>(undefined);
  const lastLoadedSourceRef = useRef<string | undefined>(undefined);
  
  // Auto-populate user_lookup fields with current user on mount
  useEffect(() => {
    if (field.fieldType === "user_lookup" && !value && user) {
      onChange({
        id: user.id,
        name: user.name || user.email,
        email: user.email
      });
    }
  }, [field.fieldType, user]);

  // Auto-populate readonly fields from user profile and sync to formData
  useEffect(() => {
    if (field.fieldType === "readonly" && field.optionsSource === "user_profile" && user) {
      let autoValue = "";
      const fieldCode = field.code;
      if (fieldCode === "requestor_name") {
        autoValue = user.name || user.email || "";
      } else if (fieldCode === "email") {
        autoValue = user.email || "";
      } else if (fieldCode === "company") {
        autoValue = (user as any).companyName || (user as any).subContractorCompany || "Centre3";
      } else if (fieldCode === "mobile") {
        autoValue = (user as any).phone || "";
      } else if (fieldCode === "department") {
        autoValue = (user as any).departmentName || "";
      }
      if (autoValue && autoValue !== value) {
        onChange(autoValue);
      }
    }
  }, [field.fieldType, field.optionsSource, field.code, user]);

  // Get localized text
  const getLabel = () => (isRTL && field.nameAr ? field.nameAr : field.name);
  const getPlaceholder = () =>
    isRTL && field.placeholderAr ? field.placeholderAr : field.placeholder;
  const getHelpText = () =>
    isRTL && field.helpTextAr ? field.helpTextAr : field.helpText;
  const getOptionLabel = (opt: FieldOption) =>
    isRTL && opt.labelAr ? opt.labelAr : opt.label;

  // Check if field should be visible based on conditions
  const isVisible = (): boolean => {
    if (!field.showCondition) return true;

    const { field: condField, operator = "equals", value: condValue } = field.showCondition;
    const fieldValue = formValues[condField];

    switch (operator) {
      case "equals":
        return fieldValue === condValue;
      case "not_equals":
        return fieldValue !== condValue;
      case "in":
        return Array.isArray(condValue) && condValue.includes(fieldValue);
      case "not_empty":
        return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
      case "empty":
        return fieldValue === undefined || fieldValue === null || fieldValue === "";
      default:
        return true;
    }
  };

  // Data source types that need API fetching
  const dynamicDataSources = [
    "countries", "regions", "cities", "sites", "zones", "areas",
    "departments", "groups", "users", "contractors",
    "request_types", "approval_roles",
    "user_sites", "user_groups", "user_departments",
    "material_types"
  ];

  const locationResolved = useMemo(
    () => resolveLocationDynamicSource(field),
    [field.optionsSource, field.optionsApi, field.code, field.filterByField]
  );

  // Compute stable scalar filter values to avoid re-running the effect on every formValues reference change
  const locationFilterValue = locationResolved?.filterField ? formValues[locationResolved.filterField] : undefined;
  const dependentFilterValue = field.dependsOnField ? formValues[field.dependsOnField] : undefined;
  const genericFilterValue = field.filterByField ? formValues[field.filterByField] : undefined;

  // Location sources that support single-option auto-select
  const autoSelectSources = ["regions", "cities", "sites", "zones", "areas"];

  const loadDataSourceOptions = async (source: string, filterValue?: any) => {
    // Skip re-fetch if source and filter haven't changed (prevents flicker on re-renders)
    const filterKey = filterValue !== undefined ? String(filterValue) : '';
    if (
      lastLoadedSourceRef.current === source &&
      lastLoadedFilterRef.current === filterKey &&
      dynamicOptions.length > 0
    ) {
      console.log(`[loadDataSourceOptions:${field.code}] Skipping fetch - cached options available`);
      return;
    }
    lastLoadedSourceRef.current = source;
    lastLoadedFilterRef.current = filterKey;
    setIsLoadingOptions(true);
    try {
      // Use fetch with proper batch format for tRPC
      const input = {
        "0": {
          json: {
            source,
            filterValue: filterValue ? String(filterValue) : undefined,
          }
        }
      };
      const url = `/api/trpc/requestConfig.fields.getDataSourceOptions?batch=1&input=${encodeURIComponent(JSON.stringify(input))}`;
      console.log(`[loadDataSourceOptions:${field.code}] Fetching from ${url}`);
      const response = await fetch(url, { credentials: 'include' });
      console.log(`[loadDataSourceOptions:${field.code}] Response status: ${response.status}`);
      const result = await response.json();
      console.log(`[loadDataSourceOptions:${field.code}] Response data:`, result);
      if (result[0]?.result?.data?.json) {
        const loaded: FieldOption[] = result[0].result.data.json.map((item: any) => ({
          value: String(item.value),
          label: item.label,
          labelAr: item.labelAr,
        }));
        console.log(`[loadDataSourceOptions:${field.code}] Loaded ${loaded.length} options`);
        setDynamicOptions(loaded);
        // Auto-select when exactly one option is available for location cascade fields
        // Use valueRef.current so the async callback always checks the latest value (not stale closure)
        if (
          loaded.length === 1 &&
          autoSelectSources.includes(source) &&
          (!valueRef.current || valueRef.current === "")
        ) {
          onChange(loaded[0].value);
        }
      } else {
        console.log(`[loadDataSourceOptions:${field.code}] No data in response`);
      }
    } catch (error) {
      console.error(`[loadDataSourceOptions:${field.code}] Failed to load data source options:`, error);
      setDynamicOptions([]);
    } finally {
      setIsLoadingOptions(false);
    }
  };

  const loadDependentOptions = async (parentValue: string) => {
    setIsLoadingOptions(true);
    try {
      // In a real implementation, this would call the API
      await new Promise((resolve) => setTimeout(resolve, 500));
      setDynamicOptions([]);
    } catch (error) {
      console.error("Failed to load options:", error);
    } finally {
      setIsLoadingOptions(false);
    }
  };

  // Load options from data source when field mounts or filter changes
  // Use stable scalar values (not the whole formValues object) to avoid infinite re-render loops
  useEffect(() => {
    console.log(`[FieldRenderer] Loading options for field: ${field.code}, type: ${field.fieldType}, optionsSource: ${field.optionsSource}, locationResolved:`, locationResolved);

    if (locationResolved) {
      const filterField = locationResolved.filterField;
      console.log(`[FieldRenderer:${field.code}] Location resolved: source=${locationResolved.source}, filterField=${filterField}, filterValue=${locationFilterValue}`);

      if (filterField && !locationFilterValue) {
        console.log(`[FieldRenderer:${field.code}] Waiting for parent filter field: ${filterField}`);
        setDynamicOptions([]);
        return;
      }
      console.log(`[FieldRenderer:${field.code}] Calling loadDataSourceOptions(${locationResolved.source}, ${locationFilterValue})`);
      loadDataSourceOptions(locationResolved.source, locationFilterValue);
      return;
    }

    const source = field.optionsSource;
    if (!source || source === "static") {
      console.log(`[FieldRenderer:${field.code}] No dynamic source or static source, skipping`);
      return;
    }

    if (source === "dependent" && field.dependsOnField) {
      if (dependentFilterValue) {
        console.log(`[FieldRenderer:${field.code}] Loading dependent options`);
        loadDependentOptions(dependentFilterValue);
      } else {
        console.log(`[FieldRenderer:${field.code}] Waiting for dependent filter field`);
        setDynamicOptions([]);
      }
      return;
    }

    if (dynamicDataSources.includes(source)) {
      if (field.filterByField && !genericFilterValue) {
        console.log(`[FieldRenderer:${field.code}] Waiting for filter field: ${field.filterByField}`);
        setDynamicOptions([]);
        return;
      }
      console.log(`[FieldRenderer:${field.code}] Calling loadDataSourceOptions(${source}, ${genericFilterValue})`);
      loadDataSourceOptions(source, genericFilterValue);
    }
  }, [
    locationResolved,
    locationFilterValue,
    dependentFilterValue,
    genericFilterValue,
    field.optionsSource,
    field.dependsOnField,
    field.filterByField,
    field.optionsApi,
    field.code,
  ]);

  const effectiveFilterField = locationResolved?.filterField ?? field.filterByField;
  // A field is waiting for its parent only if the parent field is truly empty.
  const parentValue = effectiveFilterField ? (formValues[effectiveFilterField] ?? '') : '';
  const isWaitingForParent =
    Boolean(effectiveFilterField) &&
    !parentValue &&
    (locationResolved !== null ||
      field.optionsSource === "dependent" ||
      dynamicDataSources.includes(field.optionsSource || ""));
  // Map field codes to translated names for "Select X first" placeholder
  const locationFieldNameMap: Record<string, { en: string; ar: string }> = {
    country: { en: 'Country', ar: 'الدولة' },
    region:  { en: 'Region',  ar: 'المنطقة' },
    city:    { en: 'City',    ar: 'المدينة' },
    site:    { en: 'Site',    ar: 'الموقع' },
    zone:    { en: 'Zone',    ar: 'المنطقة' },
    Zone:    { en: 'Zone',    ar: 'المنطقة' },
    area:    { en: 'Area',    ar: 'المنطقة الفرعية' },
  };
  const parentFieldName = effectiveFilterField
    ? (isRTL
        ? (locationFieldNameMap[effectiveFilterField]?.ar || effectiveFilterField)
        : (locationFieldNameMap[effectiveFilterField]?.en || effectiveFilterField.charAt(0).toUpperCase() + effectiveFilterField.slice(1)))
    : "";

  // Get options to display
  const getOptions = (): FieldOption[] => {
    const source = field.optionsSource;
    if (source && source !== "static") {
      return dynamicOptions;
    }
    return field.options || [];
  };

  if (!isVisible()) return null;

  // (Country and city are now normal interactive dropdowns — no hardcoded read-only override)

  const baseInputClass = cn(
    "w-full",
    error && "border-[#FF6B6B] focus:ring-[#FF6B6B]"
  );

  // Render based on field type
  switch (field.fieldType) {
    case "text":
    case "email":
    case "phone":
      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
            {getLabel()}
            {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
          </Label>
          <Input
            type={field.fieldType === "email" ? "email" : "text"}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={getPlaceholder()}
            disabled={disabled}
            className={baseInputClass}
            maxLength={field.validation?.maxLength}
          />
          {getHelpText() && (
            <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
          )}
          {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
        </div>
      );

    case "number":
      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
            {getLabel()}
            {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
          </Label>
          <Input
            type="number"
            value={value || ""}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : "")}
            placeholder={getPlaceholder()}
            disabled={disabled}
            className={baseInputClass}
            min={field.validation?.min}
            max={field.validation?.max}
          />
          {getHelpText() && (
            <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
          )}
          {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
        </div>
      );

    case "textarea":
      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
            {getLabel()}
            {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
          </Label>
          <Textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={getPlaceholder()}
            disabled={disabled}
            className={cn(baseInputClass, "min-h-[100px]")}
            maxLength={field.validation?.maxLength}
          />
          {getHelpText() && (
            <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
          )}
          {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
        </div>
      );

    case "date": {
      const today = new Date();
      let effectiveMinDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const baseMinDate = new Date(effectiveMinDate);
      const maxDate = maxDurationDays
        ? new Date(baseMinDate.getTime() + maxDurationDays * 24 * 60 * 60 * 1000)
        : undefined;

      // If this is an end_date field and start_date has a value, set minDate to start_date
      if (field.code === "end_date" && formValues.start_date) {
        const startDateParsed = new Date(formValues.start_date + "T00:00:00");
        if (!isNaN(startDateParsed.getTime()) && startDateParsed > effectiveMinDate) {
          effectiveMinDate = startDateParsed;
        }
      }

      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
            {getLabel()}
            {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
          </Label>
          <SapDatePicker
            value={value || ""}
            onChange={onChange}
            placeholder={getPlaceholder() || "e.g. Dec 31, 2023"}
            disabled={disabled}
            minDate={effectiveMinDate}
            maxDate={maxDate}
            error={!!error}
          />
          {field.code === "end_date" && formValues.start_date && (
            <p className="text-xs text-[#9E9E9E]">Must be on or after {new Date(formValues.start_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
          )}
          {maxDurationDays && !formValues.start_date && (
            <p className="text-xs text-[#9E9E9E]">Maximum {maxDurationDays} days from today</p>
          )}
          {getHelpText() && (
            <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
          )}
          {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
        </div>
      );
    }

    case "datetime": {
      const todayDt = new Date();
      const minDateDt = new Date(todayDt.getFullYear(), todayDt.getMonth(), todayDt.getDate());
      const maxDateDt = maxDurationDays
        ? new Date(minDateDt.getTime() + maxDurationDays * 24 * 60 * 60 * 1000)
        : undefined;
      // Split datetime value into date and time parts
      const dtParts = (value || "").split("T");
      const dateVal = dtParts[0] || "";
      const timeVal = dtParts[1] ? dtParts[1].substring(0, 5) : "";
      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
            {getLabel()}
            {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
          </Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <SapDatePicker
              value={dateVal}
              onChange={(newDate) => {
                const t = timeVal || "09:00";
                onChange(`${newDate}T${t}`);
              }}
              placeholder="Select date"
              disabled={disabled}
              minDate={minDateDt}
              maxDate={maxDateDt}
              error={!!error}
            />
            <SapTimePicker
              value={timeVal}
              onChange={(newTime) => {
                const d = dateVal || new Date().toISOString().split("T")[0];
                onChange(`${d}T${newTime}`);
              }}
              placeholder="Select time"
              disabled={disabled}
              error={!!error}
            />
          </div>
          {maxDurationDays && (
            <p className="text-xs text-[#9E9E9E]">Maximum {maxDurationDays} days from today</p>
          )}
          {getHelpText() && (
            <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
          )}
          {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
        </div>
      );
    }

    case "time": {
      // Cross-field validation: if this is end_time and dates are the same, show warning
      const isSameDateRange = field.code === "end_time" && formValues.start_date && formValues.end_date && formValues.start_date === formValues.end_date;
      const hasTimeConflict = isSameDateRange && formValues.start_time && value && value <= formValues.start_time;

      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
            {getLabel()}
            {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
          </Label>
          <SapTimePicker
            value={value || ""}
            onChange={(newTime) => onChange(newTime)}
            placeholder="Select time"
            disabled={disabled}
            error={!!error || !!hasTimeConflict}
          />
          {hasTimeConflict && (
            <p className="text-xs text-[#FF6B6B]">End time must be after start time ({formValues.start_time})</p>
          )}
          {isSameDateRange && formValues.start_time && !hasTimeConflict && value && (
            <p className="text-xs text-[#9E9E9E]">Must be after {formValues.start_time}</p>
          )}
          {getHelpText() && (
            <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
          )}
          {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
        </div>
      );
    }

    case "dropdown": {
      // Use searchable combobox for site/zone/area fields (can have many options)
      // NOTE: useState cannot be called inside switch/case, so we use a sub-component
      const isSearchable = ['sites', 'zones', 'areas'].includes(field.optionsSource || '') ||
        field.code === 'site' || field.code === 'zone' || field.code === 'area' ||
        field.code.includes('_site') || field.code.includes('_zone') || field.code.includes('_area');
      const allOpts = getOptions().filter((opt) => opt.value !== "" && opt.value != null);
      if (isSearchable) {
        return (
          <SearchableDropdownField
            field={field}
            value={value}
            onChange={onChange}
            disabled={disabled || false}
            error={error}
            isWaitingForParent={!!isWaitingForParent}
            parentFieldName={parentFieldName || ''}
            isLoadingOptions={isLoadingOptions}
            options={allOpts}
            getLabel={getLabel}
            getPlaceholder={getPlaceholder}
            getHelpText={getHelpText}
            getOptionLabel={getOptionLabel}
            baseInputClass={baseInputClass}
          />
        );
      }
      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
            {getLabel()}
            {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
          </Label>
          <Select
            value={value || ""}
            onValueChange={(newValue) => {
              onChange(newValue);
            }}
            disabled={disabled || isLoadingOptions || !!isWaitingForParent}
          >
            <SelectTrigger className={cn(baseInputClass, isWaitingForParent && "opacity-60")}>
              <SelectValue placeholder={isWaitingForParent ? (isRTL ? `اختر ${parentFieldName} أولاً` : `Select ${parentFieldName} first`) : (getPlaceholder() || t("common.select", "Select..."))} />
            </SelectTrigger>
            <SelectContent>
              {isLoadingOptions ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : (
                allOpts.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {getOptionLabel(opt)}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          {getHelpText() && (
            <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
          )}
          {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
        </div>
      );
    }

    case "dropdown_multi":
      const selectedValues = Array.isArray(value) ? value : [];
      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
            {getLabel()}
            {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
          </Label>
          <div className="border rounded-md p-3 space-y-2">
            {getOptions().map((opt) => (
              <div key={opt.value} className="flex items-center gap-2">
                <Checkbox
                  id={`${field.code}-${opt.value}`}
                  checked={selectedValues.includes(opt.value)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      onChange([...selectedValues, opt.value]);
                    } else {
                      onChange(selectedValues.filter((v: string) => v !== opt.value));
                    }
                  }}
                  disabled={disabled}
                />
                <Label
                  htmlFor={`${field.code}-${opt.value}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {getOptionLabel(opt)}
                </Label>
              </div>
            ))}
          </div>
          {selectedValues.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {selectedValues.map((v: string) => {
                const opt = getOptions().find((o) => o.value === v);
                return (
                  <Badge key={v} variant="secondary" className="text-xs">
                    {opt ? getOptionLabel(opt) : v}
                    <button
                      type="button"
                      onClick={() => onChange(selectedValues.filter((sv: string) => sv !== v))}
                      className="ml-1 hover:text-[#FF6B6B]"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
            </div>
          )}
          {getHelpText() && (
            <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
          )}
          {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
        </div>
      );

    case "radio":
      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
            {getLabel()}
            {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
          </Label>
          <RadioGroup
            value={value || ""}
            onValueChange={onChange}
            disabled={disabled}
            className="flex flex-wrap gap-4"
          >
            {getOptions().map((opt) => (
              <div key={opt.value} className="flex items-center gap-2">
                <RadioGroupItem value={opt.value} id={`${field.code}-${opt.value}`} />
                <Label
                  htmlFor={`${field.code}-${opt.value}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {getOptionLabel(opt)}
                </Label>
              </div>
            ))}
          </RadioGroup>
          {getHelpText() && (
            <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
          )}
          {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
        </div>
      );

    case "checkbox":
      return (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <Checkbox
              id={field.code}
              checked={value === true || value === "true"}
              onCheckedChange={(checked) => onChange(checked)}
              disabled={disabled}
            />
            <Label
              htmlFor={field.code}
              className="text-sm font-medium cursor-pointer flex items-center gap-1"
            >
              {getLabel()}
              {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
            </Label>
          </div>
          {getHelpText() && (
            <p className="text-xs text-[#6B6B6B] ml-6">{getHelpText()}</p>
          )}
          {error && <p className="text-xs text-[#FF6B6B] ml-6">{error}</p>}
        </div>
      );

    case "checkbox_group":
      const checkedValues = Array.isArray(value) ? value : [];
      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
            {getLabel()}
            {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
          </Label>
          <div className="border rounded-md p-3 space-y-2.5">
            {getOptions().map((opt) => (
              <div key={opt.value} className="flex items-start gap-2">
                <Checkbox
                  id={`${field.code}-${opt.value}`}
                  checked={checkedValues.includes(opt.value)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      onChange([...checkedValues, opt.value]);
                    } else {
                      onChange(checkedValues.filter((v: string) => v !== opt.value));
                    }
                  }}
                  disabled={disabled}
                  className="mt-0.5"
                />
                <Label
                  htmlFor={`${field.code}-${opt.value}`}
                  className="text-sm font-normal cursor-pointer leading-tight"
                >
                  {getOptionLabel(opt)}
                </Label>
              </div>
            ))}
          </div>
          {getHelpText() && (
            <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
          )}
          {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
        </div>
      );

    case "file":
    case "file_multi": {
      // Files are stored as URL strings after S3 upload.
      // During upload, a sentinel { uploading: true, name } object is placed in state.
      const isMulti = field.fieldType === "file_multi";
      const maxFiles = field.validation?.maxFiles || (isMulti ? 10 : 1);
      const urls: string[] = Array.isArray(value)
        ? value.filter((v: any) => typeof v === "string" && v.startsWith("http"))
        : typeof value === "string" && value.startsWith("http")
        ? [value]
        : [];
      const isUploading = Array.isArray(value)
        ? value.some((v: any) => v && typeof v === "object" && v.uploading)
        : !!(value && typeof value === "object" && (value as any).uploading);

      const uploadMutation = trpc.requests.uploadAttachment.useMutation({
        onSuccess: (data: { url: string }) => {
          if (isMulti) {
            const current = Array.isArray(value)
              ? value.filter((v: any) => typeof v === "string" && v.startsWith("http"))
              : [];
            onChange([...current, data.url]);
          } else {
            onChange(data.url);
          }
        },
        onError: (err: any) => {
          toast.error(`Upload failed: ${err.message}`);
          if (!isMulti) onChange(null);
          else {
            onChange(Array.isArray(value)
              ? value.filter((v: any) => typeof v === "string" && v.startsWith("http"))
              : []);
          }
        },
      });

      const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = Array.from(e.target.files || []);
        if (selected.length === 0) return;
        // Mark uploading state so UI shows spinner
        if (isMulti) {
          onChange([...urls, ...selected.map((f) => ({ name: f.name, uploading: true }))]);
        } else {
          onChange({ name: selected[0].name, uploading: true });
        }
        const toUpload = selected.slice(0, maxFiles - urls.length);
        toUpload.forEach((file) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = (reader.result as string).split(",")[1];
            uploadMutation.mutate({
              fileName: file.name,
              mimeType: file.type || "application/octet-stream",
              base64Data: base64,
              context: field.code,
            });
          };
          reader.readAsDataURL(file);
        });
        e.target.value = "";
      };

      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
            {getLabel()}
            {Boolean(field.isRequired) && <span className="text-[#FF6B6B]">*</span>}
          </Label>
          <div className="border-2 border-dashed border-[#E0E0E0] rounded-lg p-4 text-center hover:border-[#5B2C93]/50 transition-colors">
            <input
              type="file"
              id={field.code}
              multiple={isMulti}
              accept={field.validation?.accept}
              onChange={handleFileSelect}
              disabled={disabled || isUploading || urls.length >= maxFiles}
              className="hidden"
            />
            <label
              htmlFor={field.code}
              className={`cursor-pointer flex flex-col items-center gap-2 ${
                disabled || isUploading || urls.length >= maxFiles ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isUploading ? (
                <Loader2 className="h-8 w-8 text-[#5B2C93] animate-spin" />
              ) : (
                <Upload className="h-8 w-8 text-[#9CA3AF]" />
              )}
              <span className="text-sm text-[#6B6B6B]">
                {isUploading
                  ? t("common.uploading", "Uploading...")
                  : t("common.clickToUpload", "Click to upload")}
                {isMulti && !isUploading && ` (${urls.length}/${maxFiles})`}
              </span>
              {field.validation?.accept && !isUploading && (
                <span className="text-xs text-[#9CA3AF]">{field.validation.accept}</span>
              )}
            </label>
          </div>
          {urls.length > 0 && (
            <div className="space-y-2 mt-2">
              {urls.map((url: string, index: number) => {
                const rawName = url.split("/").pop()?.split("?")[0] || `File ${index + 1}`;
                const displayName = decodeURIComponent(rawName).replace(/^\d+-/, "");
                return (
                  <div key={index} className="flex items-center gap-2 p-2 bg-[#F5F5F5] rounded">
                    <FileText className="h-4 w-4 text-[#5B2C93] shrink-0" />
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const res = await fetch(
                            `/api/trpc/requests.getAttachmentUrl?batch=1&input=${encodeURIComponent(JSON.stringify({ "0": { json: { url } } }))}`,
                            { credentials: "include" }
                          );
                          const data = await res.json();
                          const downloadUrl = data[0]?.result?.data?.json?.downloadUrl || url;
                          window.open(downloadUrl, "_blank", "noopener,noreferrer");
                        } catch {
                          window.open(url, "_blank", "noopener,noreferrer");
                        }
                      }}
                      className="text-sm flex-1 truncate text-[#5B2C93] hover:underline text-left"
                    >
                      {displayName}
                    </button>
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => {
                          if (isMulti) {
                            onChange(urls.filter((_: any, i: number) => i !== index));
                          } else {
                            onChange(null);
                          }
                        }}
                        className="text-[#9CA3AF] hover:text-[#FF6B6B] shrink-0"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {getHelpText() && <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>}
          {error && <p className="text-xs text-[#FF6B6B]">{error}</p>}
        </div>
      );
    }

    case "user_lookup":
      return (
        <UserLookupField
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
          error={error}
          isRTL={isRTL}
          getLabel={getLabel}
          getPlaceholder={getPlaceholder}
          getHelpText={getHelpText}
        />
      );

    case "readonly": {
      // Display value: prefer formData value (synced by useEffect above), then user profile, then default
      let displayValue = value || field.defaultValue || "";
      if (field.optionsSource === "user_profile" && user) {
        const fieldCode = field.code;
        if (fieldCode === "requestor_name") {
          displayValue = value || user.name || user.email || "";
        } else if (fieldCode === "email") {
          displayValue = value || user.email || "";
        } else if (fieldCode === "company") {
          displayValue = value || (user as any).companyName || (user as any).subContractorCompany || "Centre3";
        } else if (fieldCode === "mobile") {
          displayValue = value || (user as any).phone || "";
        } else if (fieldCode === "department") {
          displayValue = value || (user as any).departmentName || "";
        }
      }
      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-[#2C2C2C]">
            {getLabel()}
          </Label>
          <Input
            type="text"
            value={displayValue}
            readOnly
            className="bg-[#F5F5F5]"
          />
          {getHelpText() && (
            <p className="text-xs text-[#6B6B6B]">{getHelpText()}</p>
          )}
        </div>
      );
    }

    case "site_type":
      return (
        <SiteTypeField
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
          error={error}
          isRTL={isRTL}
          getLabel={getLabel}
          getHelpText={getHelpText}
        />
      );

    case "zone_type":
      return (
        <ZoneTypeField
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
          error={error}
          isRTL={isRTL}
          getLabel={getLabel}
          getHelpText={getHelpText}
        />
      );

    case "area_type":
      return (
        <AreaTypeField
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
          error={error}
          isRTL={isRTL}
          getLabel={getLabel}
          getHelpText={getHelpText}
        />
      );

    case "multi_user_lookup":
      return (
        <MultiUserLookupField
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
          error={error}
          isRTL={isRTL}
          getLabel={getLabel}
          getPlaceholder={getPlaceholder}
          getHelpText={getHelpText}
        />
      );

    case "activity_selector":
      return (
        <ActivitySelectorField
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
          error={error}
          isRTL={isRTL}
          getLabel={getLabel}
          getHelpText={getHelpText}
        />
      );

    default:
      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-[#2C2C2C]">
            {getLabel()} (Unsupported: {field.fieldType})
          </Label>
          <Input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={baseInputClass}
          />
        </div>
      );
  }
}
